import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { ChecklistServicoAmbienteModel } from "../../domain/models/checklist_servico_ambiente";
import { useChecklistServicoAmbienteListaFilters } from "../hooks/checklist_servico_ambiente_lista/filters";
import { useChecklistServicoAmbienteListaPagination } from "../hooks/checklist_servico_ambiente_lista/pagination";
import { ChecklistServicoDetalhe } from "../../domain/models/checklist_servico_detalhe";
import { ChecklistServicoAmbienteCadastroForm } from "../schemas/checklist_servico_ambiente_lista/checklistServicoAmbienteCadastro";
import { ChecklistServicoAmbienteCopiaForm } from "../schemas/checklist_servico_ambiente_lista/checklistServicoAmbienteCopia";

type LoadChecklistServicoAmbienteListaState = {
    idChecklistServico: number;
    descricao?: string,

    currentPage?: number;
    rowsPerPage?: number;
}

export const useChecklistServicoAmbienteListaViewModel = () => {
    const {
        obras: {
            getAllChecklistServicoAmbienteUseCase,
            getChecklistServicoAmbienteByIdUseCase,
            updateChecklistServicoAmbienteUseCase,
            createChecklistServicoAmbienteUseCase,
            copyChecklistServicoAmbienteUseCase,
            deleteChecklistServicoAmbienteUseCase,
            getChecklistServicoByIdUseCase,
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        }
    } = useDependencyInjection();

    const backOrNavigate = useBackOrNavigate();

    const { pushErrorNotification, pushInfoNotification } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const location = useLocation();
    const navigate = useBackOrNavigate();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCopiaModal, setOpenCopiaModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const [selectedIdChecklistServicoAmbiente, setSelectedIdChecklistServicoAmbiente] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [checklistServicoAmbienteLista, setChecklistServicoAmbienteLista] = useState<ChecklistServicoAmbienteModel[]>();
    const [selectedChecklistServicoAmbiente, setSelectedChecklistServicoAmbiente] = useState<ChecklistServicoAmbienteModel | undefined>();
    const [selectedChecklistServico, setSelectedChecklistServico] = useState<ChecklistServicoDetalhe | undefined>();

    const [selectedIdChecklistServico, setSelectedIdChecklistServico] = useState<number | undefined>();

    const filters = useChecklistServicoAmbienteListaFilters();
    const pagination = useChecklistServicoAmbienteListaPagination();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    //#region CARREGAMENTO.

    const loadChecklistServicoAmbienteLista = useCallback(async (funcState?: LoadChecklistServicoAmbienteListaState) => {
        try {
            setLoading(true);
            const selectedIdChecklistServicoFilter = funcState?.idChecklistServico ?? selectedIdChecklistServico;
            const filteredDescricao = funcState?.descricao ?? filters.descricao;

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllChecklistServicoAmbienteUseCase.execute(
                selectedIdChecklistServicoFilter ?? 0,
                filteredDescricao,
                filteredPage,
                filteredRowsPerPage,
            );

            setChecklistServicoAmbienteLista(getAllChecklistServicoAmbienteUseCase.items);

            pagination.setRowsPerPage(getAllChecklistServicoAmbienteUseCase.pageSize);
            pagination.setTotalItems(getAllChecklistServicoAmbienteUseCase.totalItems);
        } catch (e: unknown) {
            setChecklistServicoAmbienteLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de checklist de servico!"));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        selectedIdChecklistServico,
        pagination,
        getAllChecklistServicoAmbienteUseCase,
        pushErrorNotification
    ]);

    const loadChecklistServicoAmbienteById = useCallback(async (idChecklist: number) => {
        try {
            setLoading(true);
            const checklist = await getChecklistServicoAmbienteByIdUseCase.execute(idChecklist);
            setSelectedChecklistServicoAmbiente(checklist);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Ambiente do checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [
        getChecklistServicoAmbienteByIdUseCase,
        pushErrorNotification
    ]);

    const loadChecklistServicoById = useCallback(async (idChecklistServico: number) => {
        try {
            setLoading(true);
            const checklistServico = await getChecklistServicoByIdUseCase.getChecklistServicoById(idChecklistServico);
            setSelectedChecklistServico(checklistServico);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [
        getChecklistServicoByIdUseCase,
        pushErrorNotification
    ]);

    //#endregion



    //#region PAGE START
    usePageStartBootstrap(async () => {
        try {
            const params = new URLSearchParams(location.search);
            const paramId = params.get('id');
            const locationId = paramId
                ? parseInt(paramId, 10)
                : undefined;

            if (locationId !== undefined) {
                setSelectedIdChecklistServico(locationId);
                await loadChecklistServicoById(locationId);
                await loadChecklistServicoAmbienteLista(
                    locationId ? { idChecklistServico: locationId } : undefined
                );
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de checklist de servico de ambiente!")[0]);
        }
    });

    const handleBack = useCallback(() => {
        navigate(`/ChecklistServico_lista`);
    }, [navigate]);

    //#endregion




    //#region NOVO/EDITAR.
    const handleChecklistServicoAmbienteOpenState = useCallback(async (idChecklistServicoAmbiente: number | null, openState: boolean) => {
        setOpenCadastroModal(openState);
        try {
            if (openState) {
                if (idChecklistServicoAmbiente) {
                    await loadChecklistServicoAmbienteById(idChecklistServicoAmbiente);
                } else {
                    setSelectedChecklistServicoAmbiente(undefined);
                    setSelectedIdChecklistServicoAmbiente(undefined);
                }
            } else {
                setSelectedChecklistServicoAmbiente(undefined);
                setSelectedIdChecklistServicoAmbiente(undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de novo checklist de servico de ambiente!'));
        }
    }, [
        loadChecklistServicoAmbienteById,
        setOpenCadastroModal,
        pushErrorNotification
    ]);

    const handleSubmitChecklistServicoAmbiente = useCallback(async (form: ChecklistServicoAmbienteCadastroForm) => {
        try {
            setSaving(true);
            if (selectedChecklistServicoAmbiente) {
                await updateChecklistServicoAmbienteUseCase.execute(
                    selectedChecklistServicoAmbiente?.idChecklistServicoAmbiente ?? 0,
                    form.descricao,
                    form.ordem,
                );
                pushInfoNotification("Sucesso", " Ambiente do checklist de servico atualizado com sucesso!", { isAutoFinish: true });
            } else {
                await createChecklistServicoAmbienteUseCase.execute(
                    selectedIdChecklistServico ?? 0,
                    form.descricao,
                    form.ordem,
                );
                pushInfoNotification("Sucesso", " Ambiente do checklist de servico criado com sucesso!", { isAutoFinish: true });
            }
            await loadChecklistServicoAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar ambiente do Checklist de servico!"));
        } finally {
            setSaving(false);
            setOpenCadastroModal(false);
            setSelectedChecklistServicoAmbiente(undefined);
        }
    }, [
        selectedChecklistServicoAmbiente,
        selectedIdChecklistServico,
        updateChecklistServicoAmbienteUseCase,
        createChecklistServicoAmbienteUseCase,
        loadChecklistServicoAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion




    //#region PAGINAÇÃO.
    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadChecklistServicoAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [
        loadChecklistServicoAmbienteLista,
        pushErrorNotification
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadChecklistServicoAmbienteLista({ idChecklistServico: selectedIdChecklistServico ?? 0, currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistServico, loadChecklistServicoAmbienteLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadChecklistServicoAmbienteLista({ idChecklistServico: selectedIdChecklistServico ?? 0, rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de ambientes do checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistServico, loadChecklistServicoAmbienteLista, pushErrorNotification]);

    //#endregion



    //#region COPIA
    const handleChecklistServicoAmbienteCopiaOpenState = useCallback((idChecklistServicoAmbiente: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistServicoAmbiente) {
                setSelectedIdChecklistServicoAmbiente(idChecklistServicoAmbiente);
                const checklistServico = checklistServicoAmbienteLista?.find((item) => item.idChecklistServicoAmbiente === idChecklistServicoAmbiente);
                setSelectedChecklistServicoAmbiente(checklistServico);
                setOpenCopiaModal(true);
            } else {
                setSelectedIdChecklistServicoAmbiente(undefined);
                setSelectedChecklistServicoAmbiente(undefined);
                setOpenCopiaModal(false);
            }
        }
        setOpenCopiaModal(openState);
    }, [
        checklistServicoAmbienteLista,
    ]);

    const handleSubmitChecklistServicoAmbienteCopia = useCallback(async (form: ChecklistServicoAmbienteCopiaForm) => {
        try {
            setSaving(true);
            if (selectedIdChecklistServicoAmbiente) {
                await copyChecklistServicoAmbienteUseCase.execute(
                    selectedIdChecklistServicoAmbiente,
                    form.descricao ?? undefined,
                    form.ordem ?? undefined
                );
                pushInfoNotification("Sucesso", " Ambiente do Checklist de servico copiado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel copiar o Ambiente do Checklist de servico!", { isAutoFinish: true });
            }
            await loadChecklistServicoAmbienteLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Copiar Ambiente do Checklist de servico!"));
        } finally {
            setSaving(false);
            setOpenCopiaModal(false);
            setSelectedIdChecklistServicoAmbiente(undefined)
        }
    }, [
        selectedIdChecklistServicoAmbiente,
        copyChecklistServicoAmbienteUseCase,
        loadChecklistServicoAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion






    //#region DELECAO 
    const handleChecklistServicoAmbienteDeleteOpenState = useCallback((idChecklistServicoAmbiente: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistServicoAmbiente) {
                setSelectedIdChecklistServicoAmbiente(idChecklistServicoAmbiente);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdChecklistServicoAmbiente(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);

    const handleConfirmarDeletarChecklistServicoAmbiente = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdChecklistServicoAmbiente) {
                await deleteChecklistServicoAmbienteUseCase.execute(selectedIdChecklistServicoAmbiente);
                pushInfoNotification("Sucesso", " Checklist de servico deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Checklist de servico!", { isAutoFinish: true });
            }
            await loadChecklistServicoAmbienteLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Checklist de servico!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdChecklistServicoAmbiente(undefined)
        }
    }, [
        selectedIdChecklistServicoAmbiente,
        deleteChecklistServicoAmbienteUseCase,
        loadChecklistServicoAmbienteLista,
        pushErrorNotification,
        pushInfoNotification
    ]);

    //#endregion





    //#region PESQUISA.
    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadChecklistServicoAmbienteLista({ idChecklistServico: selectedIdChecklistServico ?? 0, descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, selectedIdChecklistServico, loadChecklistServicoAmbienteLista, pushErrorNotification]
    );

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadChecklistServicoAmbienteLista({
                idChecklistServico: selectedIdChecklistServico ?? 0,
                descricao: '',
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar Ambientes do checklist de servico!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        selectedIdChecklistServico,
        pagination,
        loadChecklistServicoAmbienteLista,
        pushErrorNotification
    ]);

    //#endregion 



    //#region CRIAÇÃO/EDIÇÃO ITENS

    const handleEditItens = useCallback(async (id: number | null, openNewTab: boolean) => {
        if (id) {
            if (openNewTab) {
                window.open(`/ChecklistServicoItens_cadastro?id=${id}`, "_blank", "noopener,noreferrer");;
            } else {
                backOrNavigate(`/ChecklistServicoItens_cadastro?id=${id}`, { alternativeRoute: '/ChecklistServico_lista' });
            }
        } else {
            if (openNewTab) {
                window.open(`/ChecklistServicoItens_cadastro`, '_blank', 'noopener,noreferrer');
            } else {
                backOrNavigate(`/ChecklistServicoItens_cadastro`, { alternativeRoute: '/ChecklistServico_lista' });
            }
        }
    }, [
        backOrNavigate,
    ]);

    //#endregion



    //#region History
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedChecklistServicoAmbiente?.idChecklistServicoAmbiente ?? 0, LogTipoTabela.CHECKLIST_ENTREGA_AMBIENTE, true);
            }
        }
    }, [
        selectedChecklistServicoAmbiente,
        changeHistory,
    ]);

    //#endregion

    return {
        loading,
        saving,
        checklistServicoAmbienteLista: checklistServicoAmbienteLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        selectedIdChecklistServicoAmbiente,
        openCopiaModal,
        openCadastroModal,
        selectedChecklistServicoAmbiente,
        changeHistory,
        selectedIdChecklistServico,
        selectedChecklistServico,

        handleBack,
        handleEditItens,
        handleOpenChangeHistory,
        handleSubmitChecklistServicoAmbiente,
        handleChecklistServicoAmbienteOpenState,
        handleChecklistServicoAmbienteCopiaOpenState,
        handleSubmitChecklistServicoAmbienteCopia,
        handleChecklistServicoAmbienteDeleteOpenState,
        handleConfirmarDeletarChecklistServicoAmbiente,
        handleDescricaoChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
    };
}


